import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../shared/services/ApiService';
import { io } from 'socket.io-client';

const STATUS_STEPS = ['Recibido', 'Revisado', 'Asignado', 'En proceso', 'Resuelto', 'Cerrado'];
const STATUS_COLORS = {
    'Recibido': '#95a5a6',
    'Revisado': '#3498db',
    'Asignado': '#f39c12',
    'En proceso': '#e67e22',
    'Resuelto': '#27ae60',
    'Cerrado': '#7f8c8d'
};

const CATEGORY_LABELS = {
    iluminacion: 'Iluminacion',
    bancos: 'Bancos / Mobiliario',
    basura: 'Basura / Limpieza',
    vegetacion: 'Vegetacion / Poda',
    infraestructura: 'Infraestructura',
    otro: 'Otro'
};

const SeguimientoReporte = () => {
    const { code } = useParams();
    const [trackingCode, setTrackingCode] = useState(code || '');
    const [reporte, setReporte] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        if (code) {
            searchReporte(code);
        }
    }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

    // Socket.IO public namespace para updates en tiempo real
    useEffect(() => {
        if (!reporte?.trackingCode) return;

        const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_URL_LOCAL_HOST || 'http://localhost:5001';
        const publicSocket = io(`${socketUrl}/public`, {
            transports: ['websocket', 'polling']
        });

        publicSocket.on('connect', () => {
            publicSocket.emit('reporte:subscribe', reporte.trackingCode);
        });

        publicSocket.on('reporte:status-updated', (data) => {
            if (data.trackingCode === reporte.trackingCode) {
                // Recargar datos
                searchReporte(reporte.trackingCode);
            }
        });

        return () => {
            publicSocket.emit('reporte:unsubscribe', reporte.trackingCode);
            publicSocket.disconnect();
        };
    }, [reporte?.trackingCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const searchReporte = async (codeToSearch) => {
        const searchCode = (codeToSearch || trackingCode).trim().toUpperCase();
        if (!searchCode) {
            setError('Ingrese un codigo de seguimiento');
            return;
        }

        setLoading(true);
        setError('');
        setSearched(true);

        try {
            const response = await apiService.get(`/api/reportes-ciudadanos/tracking/${searchCode}`);
            setReporte(response.data);
        } catch (err) {
            setReporte(null);
            setError(err.response?.data?.message || 'No se encontro el reporte. Verifique el codigo.');
        } finally {
            setLoading(false);
        }
    };

    const getStepIndex = (status) => STATUS_STEPS.indexOf(status);

    return (
        <div className="seguimiento-reporte">
            <div className="seguimiento-intro">
                <h1>Seguimiento de Reporte</h1>
                <p>Ingrese su codigo de seguimiento para consultar el estado de su reporte.</p>
            </div>

            <div className="search-section">
                <div className="search-box-public">
                    <input
                        type="text"
                        placeholder="Ej: RC-20260331-A3F7"
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && searchReporte()}
                        maxLength={16}
                    />
                    <button onClick={() => searchReporte()} disabled={loading}>
                        {loading ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>
                {error && (
                    <div className="search-not-found">
                        <div className="not-found-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0057A6" strokeWidth="1.5">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                <line x1="8" y1="8" x2="14" y2="14" stroke="#e74c3c" strokeWidth="2"/>
                                <line x1="14" y1="8" x2="8" y2="14" stroke="#e74c3c" strokeWidth="2"/>
                            </svg>
                        </div>
                        <h3>Reporte no encontrado</h3>
                        <p>{error}</p>
                        <div className="not-found-tips">
                            <p><strong>Sugerencias:</strong></p>
                            <ul>
                                <li>Verifique que el codigo este escrito correctamente</li>
                                <li>El formato es: <code>RC-XXXXXXXX-XXXX</code></li>
                                <li>Asegurese de incluir los guiones</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {loading && (
                <div className="seguimiento-loading">
                    <div className="spinner-reportes"></div>
                    <p>Buscando reporte...</p>
                </div>
            )}

            {reporte && !loading && (
                <div className="reporte-resultado">
                    {/* Status stepper */}
                    <div className="status-stepper">
                        {STATUS_STEPS.map((step, index) => {
                            const currentIndex = getStepIndex(reporte.status);
                            const isActive = index <= currentIndex;
                            const isCurrent = index === currentIndex;
                            return (
                                <div key={step} className={`step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
                                    <div className="step-dot" style={isActive ? { background: STATUS_COLORS[step] } : {}}>
                                        {isActive && index < currentIndex ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"/>
                                            </svg>
                                        ) : (
                                            <span>{index + 1}</span>
                                        )}
                                    </div>
                                    <span className="step-label">{step}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Info cards */}
                    <div className="reporte-info-grid">
                        <div className="info-card">
                            <h3>Estado Actual</h3>
                            <span className="status-badge-public" style={{
                                background: STATUS_COLORS[reporte.status],
                                color: 'white'
                            }}>
                                {reporte.status}
                            </span>
                        </div>

                        <div className="info-card">
                            <h3>Categoria{(reporte.categories || []).length > 1 ? 's' : ''}</h3>
                            <div className="categories-tags">
                                {(reporte.categories || []).map(c => (
                                    <span key={c} className="category-tag-public">{CATEGORY_LABELS[c] || c}</span>
                                ))}
                            </div>
                        </div>

                        <div className="info-card">
                            <h3>Ubicacion</h3>
                            <p>{reporte.locationName || 'No especificado'}</p>
                        </div>

                        <div className="info-card">
                            <h3>Fecha de Reporte</h3>
                            <p>{new Date(reporte.createdAt).toLocaleDateString('es-ES', {
                                year: 'numeric', month: 'long', day: 'numeric'
                            })}</p>
                        </div>

                        {/* Multi-brigada */}
                        {reporte.assignments && reporte.assignments.length > 0 ? (
                            reporte.assignments.map((a, idx) => (
                                <div key={idx} className="info-card">
                                    <h3>Brigada {idx + 1}</h3>
                                    <p><strong>{a.brigade || 'Asignada'}</strong></p>
                                    <div className="categories-tags" style={{ marginTop: 4 }}>
                                        {(a.categories || []).map(c => (
                                            <span key={c} className="category-tag-public">{CATEGORY_LABELS[c] || c}</span>
                                        ))}
                                    </div>
                                    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>
                                        Estado: {a.status}
                                    </p>
                                </div>
                            ))
                        ) : reporte.assignedBrigade ? (
                            <div className="info-card">
                                <h3>Brigada Asignada</h3>
                                <p>{reporte.assignedBrigade}</p>
                            </div>
                        ) : null}

                        {reporte.assignedDate && (
                            <div className="info-card">
                                <h3>Fecha Programada</h3>
                                <p>{new Date(reporte.assignedDate).toLocaleDateString('es-ES', {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                })}</p>
                            </div>
                        )}
                    </div>

                    {/* Descripcion */}
                    <div className="reporte-description-card">
                        <h3>Descripcion del Reporte</h3>
                        <p>{reporte.description}</p>
                    </div>

                    {/* Timeline */}
                    {reporte.statusHistory && reporte.statusHistory.length > 0 && (
                        <div className="reporte-timeline-public">
                            <h3>Historial de Cambios</h3>
                            <div className="timeline-list-public">
                                {[...reporte.statusHistory].reverse().map((entry, idx) => (
                                    <div key={idx} className="timeline-entry">
                                        <div className="timeline-dot-public" style={{
                                            background: STATUS_COLORS[entry.status] || '#95a5a6'
                                        }}></div>
                                        <div className="timeline-entry-content">
                                            <strong>{entry.status}</strong>
                                            {entry.comment && <p>{entry.comment}</p>}
                                            <span className="timeline-date-public">
                                                {new Date(entry.date).toLocaleString('es-ES', {
                                                    dateStyle: 'medium', timeStyle: 'short'
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {searched && !reporte && !loading && !error && (
                <div className="search-not-found">
                    <div className="not-found-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                    </div>
                    <h3>Sin resultados</h3>
                    <p>No se encontro ningun reporte con ese codigo.</p>
                </div>
            )}
        </div>
    );
};

export default SeguimientoReporte;
